import { pinSrcFor } from '../components/MapPin'
import type { PinKind } from '../components/MapPin'

/**
 * The browser half of server-rendered map prints: start a job, list the user's
 * jobs, read one, and load the pin artwork a job needs.
 *
 * Every call goes to our own API rather than to the print server directly — the
 * Tanger application key stays server-side, and the print session is exchanged
 * from the caller's Firebase token there.
 */

export type MapPrintJobStatus = 'Pending' | 'Success' | 'Error' | 'Expired'

export interface MapPrintJobView {
  id: string
  status: MapPrintJobStatus
  createdAt: string
  scale: number
  pageLabel: string
  teamId?: string | null
  teamName?: string | null
  url?: string | null
  message?: string | null
  completedAt?: string | null
}

export type FetchImpl = (url: string, init?: RequestInit) => Promise<Response>

export interface MapPrintClientDeps {
  fetchImpl?: FetchImpl
  /** A fresh Firebase ID token; printing is entitled per signed-in user. */
  getAuthToken?: () => Promise<string | null>
}

const ENDPOINT = '/api/map-prints'

async function headers(deps: MapPrintClientDeps): Promise<Record<string, string>> {
  const token = deps.getAuthToken ? await deps.getAuthToken() : null
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

const impl = (deps: MapPrintClientDeps): FetchImpl =>
  deps.fetchImpl ?? ((url, init) => fetch(url, init))

export interface StartMapPrintArgs {
  /** The print server's job description, built by `buildMapPrintRequest`. */
  payload: unknown
  scale: number
  /** Display text for the sheet, so a job row can name itself ("A3 landscape"). */
  pageLabel: string
  teamId?: string
  teamName?: string
}

/**
 * Starts a print.
 *
 * Never throws: a print is a side errand from the map, and an offline moment or
 * a rejected page size should render as a message in the panel rather than an
 * unhandled rejection somewhere up the tree.
 */
export async function startMapPrint(
  args: StartMapPrintArgs,
  deps: MapPrintClientDeps = {},
): Promise<{ job?: MapPrintJobView; error?: string }> {
  try {
    const response = await impl(deps)(ENDPOINT, {
      method: 'POST',
      headers: await headers(deps),
      body: JSON.stringify({
        payload: args.payload,
        scale: args.scale,
        pageLabel: args.pageLabel,
        ...(args.teamId ? { teamId: args.teamId } : {}),
        ...(args.teamName ? { teamName: args.teamName } : {}),
      }),
    })

    const body = await readJson(response)
    if (!response.ok) {
      const error =
        isRecord(body) && typeof body.error === 'string' ? body.error : 'print request failed'
      return { error }
    }
    return isJobView(body) ? { job: body } : { error: 'print request failed' }
  } catch {
    return { error: 'print request failed' }
  }
}

/**
 * The signed-in user's prints, newest first.
 *
 * An unreachable server reads as an empty list: the print history is a
 * convenience panel, and a failure to load it must not break the map around it.
 */
export async function listMapPrints(deps: MapPrintClientDeps = {}): Promise<MapPrintJobView[]> {
  try {
    const response = await impl(deps)(ENDPOINT, { headers: await headers(deps) })
    if (!response.ok) return []
    const body = await readJson(response)
    return Array.isArray(body) ? body.filter(isJobView) : []
  } catch {
    return []
  }
}

/** One job, or null when the server has no such job for this user. */
export async function fetchMapPrint(
  jobId: string,
  deps: MapPrintClientDeps = {},
): Promise<MapPrintJobView | null> {
  try {
    const response = await impl(deps)(`${ENDPOINT}/${encodeURIComponent(jobId)}`, {
      headers: await headers(deps),
    })
    if (!response.ok) return null
    const body = await readJson(response)
    return isJobView(body) ? body : null
  } catch {
    return null
  }
}

/**
 * How long to wait before polling a pending job again, by attempt number.
 *
 * The shape follows the two very different prints this feature has to serve. An
 * A4 at 1:10 000 finishes in a handful of seconds, so the first minute is polled
 * briskly — the user is still watching. The expensive end is minutes rather than
 * the half hour an early hand test suggested: A0 at the measured 1:50 000 cost
 * peak is ~182 s, and the most expensive sheet the server will accept at all
 * (2000 × 2000 mm, same scale) was timed at 483 s. Nobody watches those, so
 * after the first minute the interval opens up.
 *
 * It settles at half a minute rather than growing forever because the lag
 * between a job finishing and the page noticing is what the user actually
 * experiences, and a fixed ceiling bounds that lag at 30 s however long the job
 * ran. The correction from 25 minutes to 8 does not move these steps: the brisk
 * phase still covers the cheap prints whole, and the slow phase still reaches
 * the dearest one with the same bounded lag.
 */
export function nextPollDelayMs(attempt: number): number {
  if (attempt < 20) return 3000
  if (attempt < 40) return 10000
  return 30000
}

/**
 * The pin artwork a print needs, keyed by pin type.
 *
 * Fetched from the app's own assets at print time rather than bundled: the
 * sanitiser that fits this art into the print server's SVG subset runs on the
 * same bytes the map draws, so the two can never drift apart.
 *
 * The generic `pin` artwork is always included, because it is the fallback for
 * any pin whose own art is missing. Custom pins use their own teardrop;
 * unsupported glyphs are listed before submission.
 */
export async function loadPinSymbolSvgs(
  types: Iterable<string>,
  deps: MapPrintClientDeps & { withBackground?: boolean } = {},
): Promise<Record<string, string>> {
  const wanted = new Set<string>([...types, 'pin'])
  const fetcher = impl(deps)
  const withBackground = deps.withBackground ?? true

  const entries = await Promise.all(
    [...wanted].map(async (type) => {
      try {
        const response = await fetcher(pinSrcFor(type as PinKind, withBackground))
        if (!response.ok) return null
        const svg = await response.text()
        return svg.trim().length > 0 ? ([type, svg] as const) : null
      } catch {
        // One missing asset must not fail the print: the pin falls back to the
        // generic artwork, and `buildMapPrintRequest` reports anything it had to
        // leave out.
        return null
      }
    }),
  )

  return Object.fromEntries(entries.filter((e): e is readonly [string, string] => e !== null))
}

/**
 * The artwork a print needs: the drawings to print (`symbolSvgs`), and the
 * with-background drawings a pin's stored colour is judged against
 * (`symbolDesignSvgs`) — the same pair the map's sprites use, so a colour that is
 * the type's own prints as designed and any other recolours the pin. With
 * backgrounds on the two are one set and fetched once.
 */
export async function loadPrintPinArtwork(
  types: Iterable<string>,
  deps: MapPrintClientDeps & { withBackground?: boolean } = {},
): Promise<{ symbolSvgs: Record<string, string>; symbolDesignSvgs: Record<string, string> }> {
  const wanted = [...types]
  const withBackground = deps.withBackground ?? true
  const [symbolSvgs, symbolDesignSvgs] = await Promise.all([
    loadPinSymbolSvgs(wanted, deps),
    withBackground ? null : loadPinSymbolSvgs(wanted, { ...deps, withBackground: true }),
  ])
  return { symbolSvgs, symbolDesignSvgs: symbolDesignSvgs ?? symbolSvgs }
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null

function isJobView(value: unknown): value is MapPrintJobView {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.status === 'string' &&
    typeof value.createdAt === 'string'
  )
}

/**
 * A print result URL that is safe to hand to the browser, or null.
 *
 * The URL arrives from the print server by way of our own API, so it is not user
 * input today — but nothing in the type says so, and `window.open` executes a
 * `javascript:` URL in the app's own origin. One compromised or buggy link in that
 * chain would be script execution against a signed-in hunter's session, which is
 * too high a price for trusting a string that crossed two services to get here.
 *
 * Only absolute https URLs pass. That rejects `javascript:` and `data:` outright,
 * and also plain http, which has no business carrying a document a user is about to
 * open. The host is deliberately not pinned: the print server's result URLs are its
 * own to move, and a hardcoded hostname here would break the feature the day it does.
 */
export function safePrintUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string' || url.trim().length === 0) return null
  try {
    // Parsed rather than pattern-matched: a scheme check by string prefix is
    // defeated by leading whitespace, control characters and case tricks, all of
    // which the URL parser normalises before this sees the protocol.
    const parsed = new URL(url)
    return parsed.protocol === 'https:' ? parsed.toString() : null
  } catch {
    // Not an absolute URL at all.
    return null
  }
}
